import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { DragDropProvider } from "@dnd-kit/react";
import { isSortableOperation, useSortable } from "@dnd-kit/react/sortable";
import {
  Autocomplete,
  Box,
  Button,
  CssBaseline,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import DeleteIcon from "@mui/icons-material/Delete";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import FileCopyIcon from "@mui/icons-material/FileCopy";

import Topbar from "../../../../../components/topbar/TopBar";
import Header from "../../../../../components/header/Header";
import { useNavigate, useParams } from "react-router-dom";
import { get, onValue, ref, set, update } from "firebase/database";
import { db } from "../../../../../firebase";
import { tokens } from "../../../../../theme";
import SnackBar from "../../../../../components/snackbar/SnackBar";
//sidebar
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme as themeStyles } from "@mui/material/styles";
import Sidebar from "../../../../../components/sidebar/Sidebar";
import {
  LocalizationProvider,
  MobileDatePicker,
  MobileTimePicker,
} from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { formatterTimePicker } from "../../../../../helpers/date/formatterTimePicker";
import { dateFormat, timeFormat } from "../../../../../helpers/date/date";
import { createBookingCopy } from "../../../../../helpers/booking/createBookingCopy";
import { UpdateDataBackup } from "../../backup/usecases/UpdateDataBackup";
import { DuplicateOrderBackup } from "../../backup/usecases/DuplicateOrderBackup";
import { CreateReturnOrderBackup } from "../../backup/usecases/CreateReturnOrderBackup";
import { getEnterpriseAddress } from "../../../../../api/enterprise/address";
import {
  formatDatePickerValue,
  formatTimePickerValue,
} from "../components/endDateTimeUtils";
import { EndDateTimeDialog } from "../components/EndDateTimeDialog";

function normalizeSubRoute(subRoute) {
  if (!subRoute) return null;

  return {
    id: subRoute.id,
    name: subRoute.name,
    lat: subRoute.lat ?? subRoute.latitude,
    lng: subRoute.lng ?? subRoute.longitude,
    url_address: subRoute.url_address ?? subRoute.url ?? "",
  };
}

function getOrderedSubRoutesList(booking) {
  const subRoutes = booking?.subRoutes ? Object.values(booking.subRoutes) : [];
  const destination = normalizeSubRoute(booking?.destination);
  const orderedSubRoutes = subRoutes
    .map(normalizeSubRoute)
    .filter((subRoute) => subRoute?.name)
    .filter(
      (subRoute) =>
        subRoute.name !== destination?.name ||
        subRoute.lat !== destination?.lat ||
        subRoute.lng !== destination?.lng,
    )
    .sort((a, b) => Number(a?.id || 0) - Number(b?.id || 0));

  if (destination?.name) {
    orderedSubRoutes.push(destination);
  }

  return orderedSubRoutes;
}

function getDestinationValue(subRoutesList) {
  if (!Array.isArray(subRoutesList) || subRoutesList.length === 0) return "";
  if (subRoutesList.length === 1) return subRoutesList[0].name || "";

  return `${subRoutesList.length} paraderos`;
}

function arrayMove(items, fromIndex, toIndex) {
  const nextItems = [...items];
  const [movedItem] = nextItems.splice(fromIndex, 1);
  nextItems.splice(toIndex, 0, movedItem);

  return nextItems;
}

function getSortableSubRoutes(subRoutesList) {
  return subRoutesList.map((subRoute, index) => ({
    ...subRoute,
    sortableId: `booking-quote-business-sub-route-${subRoute.id || "destination"}-${index}`,
  }));
}

function getSubRoutesFromModalList(subRoutesList) {
  return subRoutesList.map(({ sortableId, ...subRoute }) => subRoute);
}

function getRouteDestination(route) {
  if (!route) return null;

  const { sortableId, id, ...destination } = route;
  return destination;
}

function isSameRoute(routeA, routeB) {
  return (
    routeA?.name === routeB?.name &&
    routeA?.lat === routeB?.lat &&
    routeA?.lng === routeB?.lng
  );
}

function getBookingRouteValues(subRoutesList) {
  const routes = Array.isArray(subRoutesList)
    ? subRoutesList
        .map((route) => {
          if (!route) return null;

          const { sortableId, ...subRoute } = route;
          return subRoute;
        })
        .filter((route) => route?.name)
    : [];

  if (routes.length === 0) {
    return {
      destination: null,
      subRoutes: [],
    };
  }

  const destination = routes[routes.length - 1];
  const subRoutes = routes
    .slice(0, -1)
    .filter((route) => !isSameRoute(route, destination))
    .map((route, index) => ({
      ...route,
      id: index + 1,
    }));

  return {
    destination: getRouteDestination(destination),
    subRoutes,
  };
}

function getNextSubRouteId(subRoutesList) {
  const ids = subRoutesList.map((subRoute) => Number(subRoute.id || 0));
  return Math.max(0, ...ids) + 1;
}

function getDateOnlyValue(value) {
  const dateValue = value?.$d || value;
  if (!dateValue) return null;

  if (dateValue instanceof Date) {
    return new Date(
      dateValue.getFullYear(),
      dateValue.getMonth(),
      dateValue.getDate(),
    );
  }

  if (typeof dateValue === "string") {
    const [day, month, year] = dateValue.split("/");
    if (!day || !month || !year) return null;
    return new Date(Number(year), Number(month) - 1, Number(day));
  }

  return null;
}

function getTimeMinutesValue(value) {
  const timeValue = value?.$d || value;
  if (!timeValue) return null;

  if (timeValue instanceof Date) {
    return timeValue.getHours() * 60 + timeValue.getMinutes();
  }

  if (typeof timeValue === "string") {
    const [hourMinute, meridiem] = timeValue.trim().split(" ");
    const [rawHour, rawMinute] = hourMinute.split(":");
    let hour = Number(rawHour);
    const minute = Number(rawMinute);

    if (Number.isNaN(hour) || Number.isNaN(minute)) return null;
    if (meridiem === "PM" && hour !== 12) hour += 12;
    if (meridiem === "AM" && hour === 12) hour = 0;

    return hour * 60 + minute;
  }

  return null;
}

function getDateTimeValue(dateValue, timeValue) {
  const date = getDateOnlyValue(dateValue);
  const minutes = getTimeMinutesValue(timeValue);

  if (!date || minutes === null) return null;

  const dateTime = new Date(date);
  dateTime.setHours(Math.floor(minutes / 60), minutes % 60, 0, 0);

  return dateTime;
}

function SubRouteSortableItem({ subRoute, index, onDelete }) {
  const { ref, handleRef, isDragging } = useSortable({
    id: String(subRoute.sortableId),
    index,
    group: "booking-quote-business-sub-routes",
  });

  return (
    <Box
      ref={ref}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        minHeight: 44,
        px: 1,
        py: 0.75,
        border: "1px solid rgba(20, 83, 45, 0.16)",
        borderRadius: 1,
        bgcolor: isDragging ? "rgba(20, 83, 45, 0.08)" : "background.paper",
      }}
    >
      <IconButton ref={handleRef} size="small" sx={{ cursor: "grab" }}>
        <DragIndicatorIcon fontSize="small" />
      </IconButton>
      <Typography variant="body2" sx={{ flex: 1 }}>
        {index + 1}. {subRoute.name}
      </Typography>
      <IconButton
        size="small"
        color="error"
        aria-label="eliminar paradero"
        onClick={() => onDelete(subRoute.sortableId)}
      >
        <DeleteIcon fontSize="small" />
      </IconButton>
    </Box>
  );
}

const AddBookingQuoteBusiness = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const themeSidebar = themeStyles();
  const matchesSM = useMediaQuery(themeSidebar.breakpoints.down("sm"));
  const navigate = useNavigate();
  const { uuid } = useParams();
  const [booking, setBooking] = useState(null);
  const [copyDialogType, setCopyDialogType] = useState(null);
  const [copyDate, setCopyDate] = useState(null);
  const [copyTime, setCopyTime] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const { t } = useTranslation();

  const [total, setTotal] = useState([]);
  const [currency, setCurrency] = useState(1);
  const [note, setNote] = useState("");
  const [dateformat, setDateFormat] = useState("");
  const [timeFormatter, setTimeFormat] = useState("");
  const [endDateTimeDialogOpen, setEndDateTimeDialogOpen] = useState(false);
  const [dateEndFormat, setDateEndFormat] = useState("");
  const [timeEndFormatter, setTimeEndFormat] = useState("");
  const [subRoutesList, setSubRoutesList] = useState([]);
  const [openSubRoutesModal, setOpenSubRoutesModal] = useState(false);
  const [subRoutesModalList, setSubRoutesModalList] = useState([]);
  const [subRouteToAdd, setSubRouteToAdd] = useState(null);
  const [subRouteInput, setSubRouteInput] = useState("");

  const [acc, setAcc] = useState("");
  const [persons, setPersons] = useState(1);
  const [typeCar, setTypeCar] = useState(1);

  useEffect(() => {
    const starCountRef = ref(db, `BookingBusiness/${uuid}`);
    onValue(starCountRef, (snapshot) => {
      let data = snapshot.val();
      if (data === null) return console.log("No Booking");

      setPersons(data.persons);
      setTypeCar(data.vehicle);
      setNote(data.note);
      setCurrency(data.currency);
      setTotal(data.total);
      const orderedSubRoutesList = getOrderedSubRoutesList(data);
      data.subRoutesList = orderedSubRoutesList;
      setSubRoutesList(orderedSubRoutesList);
      setBooking(data);
      setDateFormat(dayjs(data.date, "DD/MM/YYYY").toDate());
      setTimeFormat(formatterTimePicker(data.date, data.time));
      setDateEndFormat(
        Number(data.booking_type) === 3
          ? dayjs(data.dateEnd, "DD/MM/YYYY").toDate()
          : "",
      );
      setTimeEndFormat(
        Number(data.booking_type) === 3
          ? formatterTimePicker(data.dateEnd, data.timeEnd)
          : "",
      );
      setOriAddr({ ...data.origin, id: data.uuid, url_address: "" });
      setDestAddr({ ...data.destination, id: data.uuid + 1, url_address: "" });
      setOriRef({ ...data.origin, id: data.uuid, url_address: "" });
    });
  }, []);

  const [stateSnk, setStateSnk] = useState(false);
  const [severity, setSeverity] = useState("success");
  const [messageSnk, setMessageSnk] = useState("");
  const vertical = "top";
  const horizontal = "center";

  const snackBarDialog = (message, severity = "success") => {
    setMessageSnk(message);
    setSeverity(severity);
    setStateSnk(true);
  };

  const handleCloseSnk = () => setStateSnk(false);

  const handleOpenEndDateTimeDialog = () => {
    if (!dateEndFormat) setDateEndFormat(dateformat);
    if (!timeEndFormatter) setTimeEndFormat(timeFormatter);
    setEndDateTimeDialogOpen(true);
  };

  const handleAcceptEndDateTimeDialog = () => {
    if (!dateEndFormat || !timeEndFormatter) {
      snackBarDialog("Debe ingresar fecha y hora final", "error");
      return;
    }

    const startDate = getDateOnlyValue(dateformat);
    const endDate = getDateOnlyValue(dateEndFormat);
    const startDateTime = getDateTimeValue(dateformat, timeFormatter);
    const endDateTime = getDateTimeValue(dateEndFormat, timeEndFormatter);

    if (!startDate || !endDate) {
      snackBarDialog("Fecha final invalida", "error");
      return;
    }
    if (endDate < startDate) {
      snackBarDialog(
        "La fecha final debe ser igual o superior a la fecha inicial",
        "error",
      );
      return;
    }
    if (!startDateTime || !endDateTime || endDateTime <= startDateTime) {
      snackBarDialog("La hora final debe ser superior a la hora inicial", "error");
      return;
    }

    setBooking((currentBooking) => ({
      ...currentBooking,
      booking_type: 3,
      dateEnd: dateEndFormat,
      timeEnd: timeEndFormatter,
    }));
    setEndDateTimeDialogOpen(false);
  };

  const handleClearEndDateTimeDialog = () => {
    setDateEndFormat("");
    setTimeEndFormat("");
    setBooking((currentBooking) => ({
      ...currentBooking,
      booking_type:
        currentBooking?.booking_type && Number(currentBooking.booking_type) !== 3
          ? currentBooking.booking_type
          : 2,
      dateEnd: "",
      timeEnd: "",
    }));
    setEndDateTimeDialogOpen(false);
  };

  const handleOpenCopyDialog = (type) => {
    const bookingDateTime = getDateTimeValue(booking?.date, booking?.time);
    setCopyDate(getDateOnlyValue(booking?.date));
    setCopyTime(bookingDateTime ? dayjs(bookingDateTime) : null);
    setCopyDialogType(type);
  };

  const handleCloseCopyDialog = () => setCopyDialogType(null);

  const handleAcceptCopyDialog = async () => {
    if (!booking?.counterBooking) {
      snackBarDialog("No se encontro el pedido", "error");
      return;
    }

    const selectedDate = dayjs(copyDate);
    const selectedTime = dayjs(copyTime);
    if (
      !copyDate ||
      !copyTime ||
      !selectedDate.isValid() ||
      !selectedTime.isValid()
    ) {
      snackBarDialog("Debe ingresar una fecha y hora validas", "error");
      return;
    }

    const date = selectedDate.format("DD/MM/YYYY");
    const time = selectedTime.format("hh:mm A");

    setIsLoading(true);
    try {
      const originalSnapshot = await get(ref(db, `BookingBusiness/${uuid}`));
      if (!originalSnapshot.exists()) {
        snackBarDialog("ESTE PEDIDO YA NO EXISTE", "error");
        return;
      }

      const originalBooking = originalSnapshot.val();
      const isReturn = copyDialogType === "return";
      const response = isReturn
        ? await CreateReturnOrderBackup({
            id: Number(originalBooking.counterBooking),
            date,
            time,
          })
        : await DuplicateOrderBackup({
            id: originalBooking.counterBooking,
            date,
            time,
          });
      const newCounterBooking = isReturn ? response?.data?.id : response?.id;
      const newUuid = isReturn ? response?.data?.uuid : response?.uuid;

      if (!newCounterBooking || !newUuid) {
        snackBarDialog("No se logro crear el respaldo del pedido", "error");
        return;
      }

      const newBooking = createBookingCopy(originalBooking, {
        uuid: newUuid,
        counterBooking: newCounterBooking,
        date,
        time,
        isReturn,
      });

      await set(ref(db, `BookingBusiness/${newUuid}`), newBooking);
      setCopyDialogType(null);
      snackBarDialog(
        isReturn ? "PEDIDO DE RETORNO CREADO" : "PEDIDO DUPLICADO",
      );
    } catch (error) {
      console.log(error);
      snackBarDialog("ERROR: No se pudo crear el pedido", "error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleClickSave = async (value) => {
    try {
      const submitSubRoutesList =
        subRoutesList.length > 0
          ? subRoutesList
          : destAddr
            ? [normalizeSubRoute(destAddr)]
            : [];
      const routeValues = getBookingRouteValues(submitSubRoutesList);
      const destinationRoute =
        routeValues.destination || normalizeSubRoute(destAddr);
      value.persons = persons === "" ? 1 : Number(persons);
      value.vehicle = Number(typeCar);
      value.total = total === "" ? 0 : Number(total);
      value.cotizar = false;
      value.currency = currency;
      value.statusPayment = "ASIGNADO";
      value.note = note === "" ? "" : note.toUpperCase();
      value.date = formatDatePickerValue(value.date);
      value.time = formatTimePickerValue(value.time);
      if (Number(value.booking_type) === 3) {
        value.dateEnd = formatDatePickerValue(value.dateEnd);
        value.timeEnd = formatTimePickerValue(value.timeEnd);
      } else {
        value.dateEnd = "";
        value.timeEnd = "";
      }
      value.destination = destinationRoute;
      value.subRoutes = routeValues.subRoutes;
      delete value.subRoutesList;

      if (oriAddr.name != oriRef.name) {
        value.origin = {
          lat: oriAddr.latitude,
          lng: oriAddr.longitude,
          name: oriAddr.name,
          url_address: "",
        };
      }
      await update(ref(db, `BookingBusiness/${value.uuid}`), value);

      const backup = {
        id: value.counterBooking,
        date: value.date.$d ? dateFormat(value.date.$d) : value.date,
        time: value.time.$d ? timeFormat(value.time.$d) : value.time,
        currency: currency,
        advance: value.advance ? Number(value.advance) : 0,
        discount: value.discount ? Number(value.discount) : 0,
        total: Number(total),
        persons: Number(persons),
        note: note,
        noteAdmin: value.noteAdmin ? value.noteAdmin : "",
        originName: value.origin.name,
        originLat: value.origin.lat,
        originLng: value.origin.lng,
        destinationName: destinationRoute.name,
        destinationLat: destinationRoute.lat,
        destinationLng: destinationRoute.lng,
        subRoutes: routeValues.subRoutes,
        dateEnd: Number(value.booking_type) === 3 ? value.dateEnd : "",
        timeEnd: Number(value.booking_type) === 3 ? value.timeEnd : "",
        bookingType: value.booking_type,
      };

      await UpdateDataBackup(backup);

      snackBarDialog("COTIZACION ACTUALIZADA");

      setTimeout(() => {
        navigate(`/admin/list-book-business-quote`);
      }, 5000);
    } catch (error) {
      snackBarDialog("ERROR: NO SE ACTUALIZO COTIZACION", "error");
      console.log(error);
    }
  };

  const [oriAddr, setOriAddr] = useState(null);
  const [destAddr, setDestAddr] = useState(null);
  const [oriRef, setOriRef] = useState(null);

  const [addressApi, setAddressApi] = useState([]);

  useEffect(() => {
    if (booking) {
      getAddressEnterprise(booking.enterpriseId);
    }
  }, [booking]);

  const getAddressEnterprise = async (enterpriseId) => {
    try {
      const token = "";
      setAddressApi([]);
      const response = await getEnterpriseAddress(token, enterpriseId);
      console.log(response);

      setAddressApi(response.data);
    } catch (error) {
      console.log(error);
    }
  };

  const handleCloseSubRoutesModal = () => setOpenSubRoutesModal(false);

  const handleOpenSubRoutesModal = (routes) => {
    setSubRoutesModalList(getSortableSubRoutes(routes || []));
    setSubRouteToAdd(null);
    setSubRouteInput("");
    setOpenSubRoutesModal(true);
  };

  const handleSubRoutesDragEnd = (event) => {
    const operation = event.operation;

    if (!operation || operation.canceled || !isSortableOperation(operation)) {
      return;
    }

    const sourceIndex = operation.source.initialIndex;
    const targetIndex = operation.source.index;
    if (sourceIndex === targetIndex) return;

    setSubRoutesModalList((currentList) =>
      arrayMove(currentList, sourceIndex, targetIndex),
    );
  };

  const handleDeleteSubRoute = (sortableId) => {
    setSubRoutesModalList((currentList) =>
      currentList.filter((subRoute) => subRoute.sortableId !== sortableId),
    );
  };

  const handleAddSubRoute = (newValue) => {
    if (!newValue) {
      setSubRouteToAdd(null);
      return;
    }

    const id = getNextSubRouteId(subRoutesModalList);
    const newSubRoute = {
      id,
      name: newValue.name,
      lat: newValue.lat ?? newValue.latitude,
      lng: newValue.lng ?? newValue.longitude,
      url_address: newValue.url_address ?? newValue.url ?? "",
      sortableId: `booking-quote-business-sub-route-${id}-${Date.now()}`,
    };

    setSubRoutesModalList((currentList) => [...currentList, newSubRoute]);
    setSubRouteToAdd(null);
    setSubRouteInput("");
  };

  const handleDestinationChange = (newValue) => {
    setDestAddr(newValue);
    setSubRoutesList(newValue ? [normalizeSubRoute(newValue)] : []);
  };

  const handleSaveSubRoutesModal = () => {
    const nextSubRoutesList = getSubRoutesFromModalList(subRoutesModalList);
    const routeValues = getBookingRouteValues(nextSubRoutesList);

    if (!routeValues.destination) {
      snackBarDialog("Debe seleccionar un destino", "error");
      return;
    }

    setSubRoutesList(nextSubRoutesList);
    setDestAddr({
      ...routeValues.destination,
      latitude: routeValues.destination.lat,
      longitude: routeValues.destination.lng,
    });
    handleCloseSubRoutesModal();
  };

  return (
    <div>
      <CssBaseline />
      <div className="app">
        {!matchesSM && <Sidebar />}
        <main className="content">
          <Topbar />
          <Box m="5px">
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <Header title={"COTIZACION EMPRESA"} subtitle="" />
            </Box>
            <Box display="grid" gap="10px">
              <Box gridColumn="span 12" gridRow="span 1">
                {booking && (
                  <Box
                    backgroundColor={colors.primary[400]}
                    p={2}
                    m={1}
                    borderRadius={3}
                  >
                    <Grid container spacing={1}>
                      {/* agregar */}
                      <Grid item xs={12} sm={6}>
                        <Autocomplete
                          value={oriAddr}
                          disablePortal
                          name="addressOrigin"
                          id="combo-box-origin"
                          onChange={(e, newValue) => {
                            setOriAddr(newValue);
                          }}
                          options={addressApi}
                          getOptionLabel={(option) =>
                            `${option.name} ${option.url_address}`
                          }
                          renderInput={(params) => (
                            <TextField {...params} label={t("Origen")} />
                          )}
                        />
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <Autocomplete
                          value={destAddr}
                          disablePortal
                          name="client"
                          id="combo-box-destination"
                          open={subRoutesList.length > 1 ? false : undefined}
                          onChange={(e, newValue) => {
                            handleDestinationChange(newValue);
                          }}
                          options={addressApi}
                          getOptionLabel={(option) =>
                            `${option?.name || ""} ${
                              option?.url_address || ""
                            }`.trim()
                          }
                          isOptionEqualToValue={(option, value) =>
                            option?.id === value?.id ||
                            (option?.name === value?.name &&
                              option?.latitude === value?.latitude &&
                              option?.longitude === value?.longitude)
                          }
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label={t("Destino")}
                              InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                  <InputAdornment position="end">
                                    <IconButton
                                      edge="end"
                                      aria-label="editar paraderos"
                                      onMouseDown={(event) => {
                                        event.preventDefault();
                                      }}
                                      onClick={(event) => {
                                        event.stopPropagation();
                                        handleOpenSubRoutesModal(
                                          subRoutesList.length > 0
                                            ? subRoutesList
                                            : destAddr
                                              ? [normalizeSubRoute(destAddr)]
                                              : [],
                                        );
                                      }}
                                    >
                                      <LocationOnIcon />
                                    </IconButton>
                                    {params.InputProps.endAdornment}
                                  </InputAdornment>
                                ),
                              }}
                              value={getDestinationValue(subRoutesList)}
                              inputProps={{
                                ...params.inputProps,
                                readOnly: subRoutesList.length > 1,
                                value: getDestinationValue(subRoutesList),
                              }}
                              onClick={() => {
                                if (subRoutesList.length > 1) {
                                  handleOpenSubRoutesModal(subRoutesList);
                                }
                              }}
                              sx={{
                                cursor:
                                  subRoutesList.length > 1 ? "pointer" : "text",
                                "& .MuiInputBase-input": {
                                  cursor:
                                    subRoutesList.length > 1
                                      ? "pointer"
                                      : "text",
                                },
                              }}
                            />
                          )}
                        />
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <TextField
                          value={booking.clientName}
                          fullWidth
                          disabled
                          variant="outlined"
                          color="success"
                          type="text"
                          label={t("client")}
                          name="client"
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Grid container spacing={1}>
                          <Grid item xs={6} sm={3} md={3}>
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                              <Stack spacing={2}>
                                <MobileDatePicker
                                  label={t("date")}
                                  inputFormat="DD/MM/YYYY"
                                  name="date"
                                  value={dateformat}
                                  onChange={(newValue) => {
                                    setDateFormat(newValue);
                                    booking.date = newValue;
                                  }}
                                  renderInput={(params) => (
                                    <TextField {...params} />
                                  )}
                                />
                              </Stack>
                            </LocalizationProvider>
                          </Grid>
                          <Grid item xs={6} sm={3} md={3}>
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                              <Stack spacing={3}>
                                <MobileTimePicker
                                  label={t("time")}
                                  value={timeFormatter}
                                  name="time"
                                  onChange={(newValue) => {
                                    setTimeFormat(newValue);
                                    booking.time = newValue;
                                  }}
                                  renderInput={(params) => (
                                    <TextField
                                      {...params}
                                      InputProps={{
                                        ...params.InputProps,
                                        endAdornment: (
                                          <InputAdornment position="end">
                                            <IconButton
                                              edge="end"
                                              aria-label="hora final"
                                              onMouseDown={(event) => {
                                                event.preventDefault();
                                                event.stopPropagation();
                                              }}
                                              onClick={(event) => {
                                                event.preventDefault();
                                                event.stopPropagation();
                                                handleOpenEndDateTimeDialog();
                                              }}
                                            >
                                              <AccessTimeIcon fontSize="small" />
                                            </IconButton>
                                            {params.InputProps.endAdornment}
                                          </InputAdornment>
                                        ),
                                      }}
                                    />
                                  )}
                                />
                              </Stack>
                            </LocalizationProvider>
                          </Grid>
                          {Number(booking.booking_type) === 3 && (
                            <>
                              <Grid item xs={6} sm={3} md={3}>
                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                  <MobileDatePicker
                                    label={t("dateEnd")}
                                    inputFormat="DD/MM/YYYY"
                                    name="dateEnd"
                                    value={dateEndFormat}
                                    onChange={(newValue) => {
                                      setDateEndFormat(newValue);
                                      booking.dateEnd = newValue;
                                    }}
                                    renderInput={(params) => (
                                      <TextField {...params} />
                                    )}
                                  />
                                </LocalizationProvider>
                              </Grid>
                              <Grid item xs={6} sm={3} md={3}>
                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                  <MobileTimePicker
                                    label={t("timeEnd")}
                                    name="timeEnd"
                                    value={timeEndFormatter}
                                    onChange={(newValue) => {
                                      setTimeEndFormat(newValue);
                                      booking.timeEnd = newValue;
                                    }}
                                    renderInput={(params) => (
                                      <TextField {...params} />
                                    )}
                                  />
                                </LocalizationProvider>
                              </Grid>
                            </>
                          )}
                          <Grid item xs={6} sm={3} md={3}>
                            <TextField
                              value={persons}
                              fullWidth
                              variant="outlined"
                              color="success"
                              type="number"
                              label={t("persons")}
                              name="persons"
                              onChange={(e) => {
                                setPersons(e.target.value);
                                //booking.persons = e.target.value;
                              }}
                            />
                          </Grid>
                          <Grid item xs={6} sm={3} md={3}>
                            <FormControl fullWidth>
                              <InputLabel>{t("Model")}</InputLabel>
                              <Select
                                value={typeCar}
                                id="vehicle"
                                name="typeCar"
                                label={t("vehicle")}
                                onChange={(e) => {
                                  setTypeCar(e.target.value);
                                }}
                              >
                                <MenuItem value={1}>{t("SEDAN")}</MenuItem>
                                <MenuItem value={2}>{t("SUV")}</MenuItem>
                                <MenuItem value={3}>{t("MINIVAN")}</MenuItem>
                              </Select>
                            </FormControl>
                          </Grid>
                        </Grid>
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <TextField
                          value={note}
                          fullWidth
                          variant="outlined"
                          color="success"
                          type="text"
                          label={t("notes")}
                          name="note"
                          onChange={(e) => setNote(e.target.value)}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  aria-label="booking return"
                                  edge="end"
                                  disabled={isLoading}
                                  onClick={() => handleOpenCopyDialog("return")}
                                  style={{ marginRight: 4 }}
                                >
                                  <SwapHorizIcon />
                                </IconButton>
                                <IconButton
                                  aria-label="booking duplicate"
                                  edge="end"
                                  disabled={isLoading}
                                  onClick={() => handleOpenCopyDialog("duplicate")}
                                  style={{ marginRight: 4 }}
                                >
                                  <FileCopyIcon />
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Grid>

                      <Grid item xs={6} sm={3}>
                        <FormControl fullWidth>
                          <InputLabel>{t("currency")}</InputLabel>
                          <Select
                            value={currency}
                            id="currency"
                            name="currency"
                            label={t("currency")}
                            onChange={(e) => setCurrency(e.target.value)}
                          >
                            <MenuItem value={1}>{t("PEN")}</MenuItem>
                            <MenuItem value={2}>{t("USD")}</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <TextField
                          value={total}
                          fullWidth
                          variant="outlined"
                          color="success"
                          type="number"
                          label={t("total")}
                          onChange={(e) => setTotal(e.target.value)}
                          name="total"
                          inputProps={{ style: { fontSize: 14 } }}
                        />
                      </Grid>

                      <Grid item xs={6} sm={12} textAlign={"end"}>
                        <Button
                          color="secondary"
                          variant="contained"
                          onClick={(e) => {
                            handleClickSave(booking);
                          }}
                        >
                          {t("saveChanges")}
                        </Button>
                      </Grid>
                    </Grid>
                    <SnackBar
                      stateSnk={stateSnk}
                      vertical={vertical}
                      horizontal={horizontal}
                      duration={4000}
                      handleClose={handleCloseSnk}
                      severity={severity}
                      messageSnk={messageSnk}
                    />
                  <EndDateTimeDialog
                    open={endDateTimeDialogOpen}
                    onClose={() => setEndDateTimeDialogOpen(false)}
                    dateEnd={dateEndFormat}
                    timeEnd={timeEndFormatter}
                    dateLabel={t("dateEnd")}
                    timeLabel={t("timeEnd")}
                    onDateEndChange={setDateEndFormat}
                    onTimeEndChange={setTimeEndFormat}
                    onClear={handleClearEndDateTimeDialog}
                    onAccept={handleAcceptEndDateTimeDialog}
                    acceptLabel={t("saveChanges")}
                  />
                    <Dialog
                      open={Boolean(copyDialogType)}
                      onClose={isLoading ? undefined : handleCloseCopyDialog}
                      fullWidth
                      maxWidth="xs"
                    >
                      <DialogTitle>
                        {copyDialogType === "return"
                          ? "¿Deseas crear el pedido de retorno?"
                          : "Deseas Duplicar este Pedido?"}
                      </DialogTitle>
                      <DialogContent>
                        <Stack spacing={2} sx={{ pt: 1 }}>
                          <Typography variant="body2">
                            {copyDialogType === "return"
                              ? "Confirma o cambia la fecha y hora del retorno."
                              : "Confirma o cambia la fecha y hora del nuevo pedido."}
                          </Typography>
                          <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <MobileDatePicker
                              label={t("date")}
                              inputFormat="DD/MM/YYYY"
                              value={copyDate}
                              onChange={setCopyDate}
                              renderInput={(params) => (
                                <TextField {...params} fullWidth />
                              )}
                            />
                            <MobileTimePicker
                              label={t("time")}
                              value={copyTime}
                              onChange={setCopyTime}
                              renderInput={(params) => (
                                <TextField {...params} fullWidth />
                              )}
                            />
                          </LocalizationProvider>
                        </Stack>
                      </DialogContent>
                      <DialogActions>
                        <Button
                          type="button"
                          variant="contained"
                          color="error"
                          onClick={handleCloseCopyDialog}
                          disabled={isLoading}
                        >
                          No
                        </Button>
                        <Button
                          type="button"
                          variant="contained"
                          color="success"
                          onClick={handleAcceptCopyDialog}
                          disabled={isLoading}
                        >
                          {copyDialogType === "return"
                            ? "Sí, Crear Retorno"
                            : "Si, Duplicar Pedido"}
                        </Button>
                      </DialogActions>
                    </Dialog>
                    <Dialog
                      open={openSubRoutesModal}
                      onClose={handleSaveSubRoutesModal}
                      fullWidth
                      maxWidth="xs"
                      PaperProps={{
                        sx: {
                          height: "80vh",
                          maxHeight: "90vh",
                        },
                      }}
                    >
                      <DialogTitle>
                        <Stack
                          direction={{ xs: "column", sm: "row" }}
                          spacing={1.5}
                          sx={{
                            alignItems: { xs: "stretch", sm: "center" },
                          }}
                        >
                          <Typography variant="h6" sx={{ flexShrink: 0 }}>
                            Paraderos
                          </Typography>
                          <Autocomplete
                            value={subRouteToAdd}
                            inputValue={subRouteInput}
                            onChange={(event, newValue) => {
                              handleAddSubRoute(newValue);
                            }}
                            onInputChange={(event, newInputValue, reason) => {
                              if (reason === "reset") return;
                              setSubRouteInput(newInputValue);
                            }}
                            options={addressApi || []}
                            getOptionLabel={(option) =>
                              `${option?.name || ""} ${
                                option?.url_address || ""
                              }`.trim()
                            }
                            isOptionEqualToValue={(option, value) =>
                              option?.id === value?.id ||
                              (option?.name === value?.name &&
                                option?.latitude === value?.latitude &&
                                option?.longitude === value?.longitude)
                            }
                            sx={{ flex: 1 }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label={t("Destino")}
                                size="small"
                              />
                            )}
                          />
                        </Stack>
                      </DialogTitle>
                      <DialogContent>
                        <Stack spacing={1} sx={{ pt: 1 }}>
                          <DragDropProvider onDragEnd={handleSubRoutesDragEnd}>
                            <Stack spacing={1}>
                              {subRoutesModalList.map((subRoute, index) => (
                                <SubRouteSortableItem
                                  key={subRoute.sortableId}
                                  subRoute={subRoute}
                                  index={index}
                                  onDelete={handleDeleteSubRoute}
                                />
                              ))}
                            </Stack>
                          </DragDropProvider>
                        </Stack>
                      </DialogContent>
                      <DialogActions>
                        <Button
                          type="button"
                          variant="contained"
                          color="error"
                          onClick={handleSaveSubRoutesModal}
                        >
                          {t("Cerrar")}
                        </Button>
                      </DialogActions>
                    </Dialog>
                  </Box>
                )}
              </Box>
            </Box>
          </Box>
        </main>
      </div>
    </div>
  );
};

export default AddBookingQuoteBusiness;
